import React, { useState } from 'react';
import { useCurrentSystem, useScenes } from '../context/SystemContext';
import SceneCard from '../SceneCard';
import SceneEditor from '../SceneEditor';
import SystemPageHeader from '../components/SystemPageHeader';
import { Plus, Palette } from 'lucide-react';
import { getScenePreviewColors, getSceneType } from '../sceneUtils';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { firestore } from '../common/firebase';

function ScenesView() {
	const { currentSystem } = useCurrentSystem();
	const { scenes = [] } = useScenes(currentSystem?.uid);
	const [editingScene, setEditingScene] = useState(null);

	if (!currentSystem) {
		return (
			<div className="p-6 text-center text-gray-500 dark:text-gray-400">
				Select a system from the Systems menu to manage scenes.
			</div>
		);
	}

	const numColors = currentSystem.num_colors || 4;
	const colorNames = currentSystem.color_names || [];
	const isBlackout = !currentSystem.current_scene || currentSystem.current_scene === 0 || currentSystem.current_scene === '0';
	const cycleCount = scenes.filter((s) => getSceneType(s) === 'cycle').length;

	const doDeleteScene = async (scene) => {
		if (scene?.uid) await deleteDoc(doc(firestore, 'scenes', scene.uid));
	};

	const activateScene = async (scene) => {
		if (!currentSystem?.uid) return;
		await updateDoc(doc(firestore, 'systems', currentSystem.uid), {
			current_scene: scene.uid,
			color: getScenePreviewColors(scene),
			current_scen_name: scene.name || '',
		});
	};

	const activateBlackout = async () => {
		if (!currentSystem?.uid) return;
		const colors = Array.from({ length: numColors }, () => ({ r: 0, g: 0, b: 0 }));
		await updateDoc(doc(firestore, 'systems', currentSystem.uid), {
			current_scene: 0,
			color: colors,
			current_scen_name: 'Blackout',
		});
	};

	return (
		<div className="p-4 sm:p-6 max-w-7xl mx-auto">
			<SystemPageHeader currentSystem={currentSystem} title="Scenes" subtitle="Static scenes and animated cycles" />

			<div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 sm:p-5 shadow-sm">
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
					<h2 className="font-semibold text-lg flex items-center gap-2 text-gray-900 dark:text-gray-100">
						<Palette size={18} className="text-[#880e4f]" /> All Scenes
					</h2>
					<div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
						<button
							onClick={() => setEditingScene({ name: `Scene ${scenes.length + 1}`, type: 'single' })}
							className="inline-flex items-center justify-center gap-1.5 text-sm px-4 py-2 bg-[#880e4f] hover:bg-[#6b0b3f] text-white rounded-xl font-medium shadow-sm w-full sm:w-auto"
						>
							<Plus size={16} /> Add Scene
						</button>
						<button
							onClick={() => setEditingScene({ name: `Cycle ${cycleCount + 1}`, type: 'cycle' })}
							className="inline-flex items-center justify-center gap-1.5 text-sm px-4 py-2 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl font-medium w-full sm:w-auto"
						>
							<Plus size={16} /> Add Cycle
						</button>
					</div>
				</div>
				<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
					<SceneCard scene={null} active={isBlackout} activateScene={activateBlackout} guest={false} />
					{scenes.map((scene) => (
						<SceneCard
							key={scene.uid}
							scene={scene}
							active={currentSystem.current_scene === scene.uid}
							activateScene={activateScene}
							deleteScene={doDeleteScene}
							guest={false}
						/>
					))}
					{scenes.length === 0 && (
						<div className="col-span-full text-sm text-gray-500 dark:text-gray-400 italic p-8 text-center border border-dashed rounded-xl">
							No scenes yet. Create a single scene or cycle to start controlling lights.
						</div>
					)}
				</div>
			</div>

			{editingScene && (
				<SceneEditor
					open={!!editingScene}
					scene={editingScene}
					doneEdit={() => setEditingScene(null)}
					numColors={numColors}
					colorNames={colorNames}
				/>
			)}
		</div>
	);
}

export default ScenesView;
